import cx from 'classnames'
import React, { useEffect } from 'react'

import Switch from 'live/components/ui/action-toggle'
import LiveTooltip from 'live/components/ui/tooltip'
import i18n from 'live/services/i18n'

import {
    IBarrierStatusSpecialStatus,
    IStatusThreshold,
    IGetSystemPropertyResponse,
    IBarrierStatus
} from '../../../../../types'
import { SystemProperty } from '../../../../../types/enums'
import { ThresholdSettingsInputsStyled } from './styles'

type MinMaxHandlerFunction = (
    statusThresholds: IStatusThreshold[],
    statusThreshold: IStatusThreshold,
    index: number
) => number

type BarrierStatusWithThreshold = IBarrierStatusSpecialStatus & {
    thresholds?: IStatusThreshold
}

interface IProps {
    statusThreshold: Map<number, IStatusThreshold>
    handleInputChange: (newThresholhds: Map<number, IStatusThreshold>) => void
    intermediaryStatusMap: Map<string, IBarrierStatusSpecialStatus>
    editing: boolean
    systemProperties: IGetSystemPropertyResponse
    min?: MinMaxHandlerFunction
    max?: MinMaxHandlerFunction
    isQualitative?: boolean
    maxLabel?: string
    minLabel?: string
    barrierCustomNameDictionary: Map<string, string>
    barrierStatusList: IBarrierStatusSpecialStatus[]
}

export const ThresholdSettingsInputs: React.FC<IProps> = ({
    statusThreshold,
    handleInputChange,
    editing,
    systemProperties,
    isQualitative = false,
    barrierStatusList
}) => {
    const priorityOrderSortFunction = (s1, s2) => s1.priorityOrder - s2.priorityOrder

    const getNextInUse = (
        barrierStatusList: BarrierStatusWithThreshold[],
        index: number
    ): BarrierStatusWithThreshold => {
        for (let i = index + 1; i < barrierStatusList.length; i++) {
            if (barrierStatusList[i].thresholds != null) {
                return barrierStatusList[i]
            }
        }
    }

    const handleMaxPfdAllowed = (threshold: number): number => {
        if (threshold) {
            return threshold - systemProperties.settingsProperties.step
        } else if (systemProperties.property === SystemProperty.ABSOLUTE) {
            return 1
        }

        return null
    }

    const generateTooltipMessage = (barrierStatus: IBarrierStatusSpecialStatus, isInUse: boolean): string => {
        if (barrierStatus.priorityOrder === 1000 || barrierStatus.priorityOrder === -1000) {
            return i18n(`You can't inactivate this status`)
        }
        if (isInUse) {
            return i18n(`Click to inactivate this status`)
        } else {
            return i18n(`Click to activate this status`)
        }
    }

    return (
        <ThresholdSettingsInputsStyled>
            <div className={cx('thresholds-header', { editing })}>
                <div className="header-min">{`${isQualitative ? 'CR' : 'PFD'} min`}</div>
                <div className="header-max">{`${isQualitative ? 'CR' : 'PFD'} max`}</div>
                <div className="header-status">{i18n('Status')}</div>
                <div className="header-color">{i18n('Color')}</div>
            </div>
            <div className="status-thresholds-content">
                {statusThreshold != null &&
                    barrierStatusList
                        ?.filter(barrierStatus => barrierStatus.priorityOrder !== 2000)
                        .sort(priorityOrderSortFunction)
                        .map(barrierStatus => ({ ...barrierStatus, thresholds: statusThreshold.get(barrierStatus.id) }))
                        .map((barrierStatus, index, self) => {
                            const isInUse = !!barrierStatus.thresholds
                            const nextBarrierStatus = getNextInUse(self, index)

                            return (
                                <div key={index} className={cx('status-thresholds-bundle', { editing })}>
                                    {editing && (
                                        <LiveTooltip
                                            content={generateTooltipMessage(barrierStatus, isInUse)}
                                            position="top"
                                        >
                                            <div className="bundle-switch">
                                                <Switch
                                                    disabled={barrierStatus.specialStatus}
                                                    active={barrierStatus.specialStatus ? true : isInUse}
                                                    onChange={() => {
                                                        if (isInUse) {
                                                            statusThreshold.delete(barrierStatus.id)
                                                        } else {
                                                            statusThreshold.set(barrierStatus.id, {
                                                                barrierStatus: barrierStatus as IBarrierStatus,
                                                                threshold: nextBarrierStatus?.thresholds?.threshold
                                                            })
                                                        }
                                                        handleInputChange(statusThreshold)
                                                    }}
                                                />
                                            </div>
                                        </LiveTooltip>
                                    )}

                                    <input
                                        type="number"
                                        className="bundle-min"
                                        value={
                                            isInUse
                                                ? barrierStatus.priorityOrder === -1000
                                                    ? isQualitative
                                                        ? 100
                                                        : systemProperties.property === SystemProperty.RELATIVE
                                                        ? barrierStatus.thresholds?.threshold
                                                        : 0
                                                    : barrierStatus.thresholds?.threshold
                                                : ''
                                        }
                                        disabled
                                        placeholder={!isInUse ? (isQualitative ? i18n('no CR') : i18n('no PFD')) : ''}
                                    />

                                    <input
                                        type="number"
                                        className={`bundle-max ${barrierStatus.priorityOrder === 1000 ? 'last' : ''} ${
                                            barrierStatus.priorityOrder === 1000 &&
                                            systemProperties.property === SystemProperty.RELATIVE &&
                                            !isQualitative
                                                ? 'last-relative'
                                                : ''
                                        } ${
                                            isQualitative
                                                ? barrierStatus?.thresholds?.threshold <
                                                      nextBarrierStatus?.thresholds?.threshold ||
                                                  barrierStatus?.thresholds?.threshold >
                                                      self[0].thresholds?.threshold ||
                                                  barrierStatus?.thresholds?.threshold <
                                                      self[self.length - 1]?.thresholds?.threshold ||
                                                  nextBarrierStatus?.thresholds?.threshold ===
                                                      self[index]?.thresholds?.threshold
                                                    ? 'red-border'
                                                    : ''
                                                : barrierStatus?.thresholds?.threshold >
                                                      nextBarrierStatus?.thresholds?.threshold ||
                                                  barrierStatus?.thresholds?.threshold <
                                                      self[0]?.thresholds?.threshold ||
                                                  nextBarrierStatus?.thresholds?.threshold >
                                                      self[self.length - 1]?.thresholds?.threshold ||
                                                  nextBarrierStatus?.thresholds?.threshold <=
                                                      self[index]?.thresholds?.threshold
                                                ? 'red-border'
                                                : ''
                                        } `}
                                        value={
                                            isInUse
                                                ? barrierStatus.priorityOrder === 1000
                                                    ? isQualitative
                                                        ? 0
                                                        : 1
                                                    : nextBarrierStatus?.thresholds?.threshold
                                                : ''
                                        }
                                        onChange={e => {
                                            const newValue = nextBarrierStatus?.thresholds
                                            if (newValue != null) {
                                                newValue.threshold = Number(e.target.value)
                                                statusThreshold.set(nextBarrierStatus.id, newValue)
                                                handleInputChange(statusThreshold)
                                            }
                                        }}
                                        disabled={barrierStatus.priorityOrder === 1000 || !isInUse}
                                        placeholder={!isInUse ? (isQualitative ? i18n('no CR') : i18n('no PFD')) : ''}
                                    />
                                    <div className="bundle-equals"> = </div>
                                    <input
                                        className="bundle-status"
                                        disabled={true}
                                        value={barrierStatus.statusCustomName ?? barrierStatus.name}
                                    />
                                    <div
                                        className="bundle-color"
                                        style={{ background: barrierStatus.colorReference }}
                                    ></div>
                                </div>
                            )
                        })}
            </div>
        </ThresholdSettingsInputsStyled>
    )
}
